import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import HeroDashboard from "@/components/HeroDashboard";
import { useAI } from "@/context/AIContext";
import { api } from "@/lib/apiClient";
import {
  Search, ArrowRight, ShieldCheck, Zap, Boxes, Sparkles, CircuitBoard, Cog, Radar, Move3D,
} from "lucide-react";

const CATEGORIES = [
  { label: "Components", icon: CircuitBoard, desc: "Boards, drivers, gearboxes & structural parts." },
  { label: "Motors", icon: Cog, desc: "Stepper, BLDC and servo motors with full specs." },
  { label: "Sensors", icon: Radar, desc: "LiDAR, IMU, encoders and vision modules." },
  { label: "Actuators", icon: Move3D, desc: "Linear, rotary, pneumatic and grippers." },
];

export default function Landing() {
  const { openAI } = useAI();
  const [q, setQ] = useState("");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const search = (e) => {
    e.preventDefault();
    openAI(q.trim() ? q.trim() : "Help me source parts for my robot build.");
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#1C2A38]/10">
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 py-16 sm:px-8 lg:grid-cols-[1fr_1.15fr] lg:py-24">
          <div className="float-in">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/55 px-3.5 py-1.5 backdrop-blur">
              <span className="live-dot h-2 w-2 rounded-full bg-[var(--teal)]" />
              <span className="font-mono-tech text-xs text-[#3c4a5a]">AI-powered robotics sourcing</span>
            </div>
            <h1 className="font-display text-4xl font-800 leading-[1.05] tracking-tight text-steel sm:text-5xl lg:text-6xl">
              Smarter Sourcing,
              <br />
              Faster Builds.
            </h1>
            <p className="mt-6 max-w-md text-base leading-relaxed text-[#33445a]">
              Source every component, actuator, sensor and motor your robot needs — from verified
              suppliers, with a live AI assistant that finds the right part in seconds.
            </p>

            <form onSubmit={search} className="mt-8 flex max-w-md items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5b6878]" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Devices, parts, suppliers…"
                  data-testid="hero-search-input"
                  className="h-12 border-[#1C2A38]/15 bg-white/80 pl-10 text-[#1C2A38] placeholder:text-[#8593a3] focus-visible:ring-[var(--teal)]"
                />
              </div>
              <Button type="submit" data-testid="hero-search-btn" className="h-12 bg-[var(--teal)] px-5 font-600 text-[#063b34] hover:bg-[#1ee9cf]">
                Search
              </Button>
            </form>

            <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-[#33445a]">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[var(--teal-deep)]" /> Verified suppliers</span>
              <span className="flex items-center gap-2"><Zap className="h-4 w-4 text-[var(--teal-deep)]" /> Real-time stock</span>
              <span className="flex items-center gap-2"><Boxes className="h-4 w-4 text-[var(--teal-deep)]" /> CAD ready</span>
            </div>
          </div>

          <div className="relative lg:h-[460px]">
            <div className="lg:absolute lg:-right-24 lg:top-1/2 lg:w-[640px] lg:-translate-y-1/2">
              <HeroDashboard onOpenAI={openAI} />
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-b border-[#1C2A38]/10 bg-[var(--surface)]">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px overflow-hidden px-5 sm:px-8 lg:grid-cols-4">
          {[
            ["Components", stats?.components, "+"],
            ["Verified suppliers", stats?.suppliers, ""],
            ["Live marketplace", stats?.listings, ""],
            ["Categories", stats?.categories, ""],
          ].map(([label, val, suffix]) => (
            <div key={label} className="py-10 text-center" data-testid={`stat-${label.split(" ")[0].toLowerCase()}`}>
              <div className="font-display text-4xl font-800 text-[#1C2A38]">
                {val ?? "—"}{val ? suffix : ""}
              </div>
              <div className="mt-1 text-sm text-[#5b6878]">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <div className="mb-12 max-w-2xl">
          <h2 className="font-display text-3xl font-800 tracking-tight text-[#1C2A38]">Source by category</h2>
          <p className="mt-3 text-[#33445a]">Everything you need to design, prototype and ship robots — organized.</p>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            return (
              <button
                key={c.label}
                onClick={() => openAI(`I'm looking for ${c.label.toLowerCase()} for my robot build. What do you recommend?`)}
                data-testid={`category-${c.label.toLowerCase()}`}
                className="group rounded-2xl border border-[#1C2A38]/10 bg-[var(--surface)] p-6 text-left shadow-sm transition-all hover:-translate-y-1 hover:border-[#1C2A38]/20 hover:shadow-lg"
              >
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-[#EEF1F3]">
                  <Icon className="h-6 w-6 text-[#1C2A38]" strokeWidth={1.6} />
                </span>
                <h3 className="mt-5 font-display text-lg font-700 text-[#1C2A38]">{c.label}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#5b6878]">{c.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-[var(--teal-deep)] opacity-0 transition-opacity group-hover:opacity-100">
                  Ask ROSA AI <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* AI SECTION */}
      <section className="relative overflow-hidden border-y border-[#1C2A38]/10 bg-[var(--surface)]">
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 py-20 sm:px-8 lg:grid-cols-2">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[var(--teal)]/30 bg-[var(--teal-soft)] px-3.5 py-1.5">
              <Sparkles className="h-3.5 w-3.5 text-[var(--teal-deep)]" />
              <span className="font-mono-tech text-xs text-[var(--teal-deep)]">LIVE · AI SOURCING</span>
            </div>
            <h2 className="mt-6 font-display text-3xl font-800 leading-tight tracking-tight text-[#1C2A38] sm:text-4xl">
              Describe your build.
              <br />ROSA AI finds the parts.
            </h2>
            <p className="mt-5 max-w-md leading-relaxed text-[#33445a]">
              Ask for a motor by torque, a sensor by protocol, or a full bill of materials. ROSA AI
              recommends compatible parts and verified suppliers — instantly.
            </p>
            <Button onClick={openAI} data-testid="ai-cta-btn" className="mt-8 h-12 bg-[var(--teal)] px-6 font-600 text-[#063b34] hover:bg-[#1ee9cf]">
              Ask ROSA AI <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
          <div className="rounded-2xl border border-[#1C2A38]/10 bg-[var(--surface-2)] p-6 shadow-lg">
            <div className="flex items-center gap-2 border-b border-[#1C2A38]/10 pb-4">
              <span className="live-dot h-2.5 w-2.5 rounded-full bg-[var(--teal)]" />
              <span className="font-display text-sm font-700 text-[#1C2A38]">ROSA AI</span>
            </div>
            <div className="mt-4 space-y-3 text-sm">
              <div className="ml-auto w-fit max-w-[85%] rounded-2xl bg-[var(--teal)] px-4 py-2.5 text-[#04221b]">
                Best motor for a 5kg robot arm joint?
              </div>
              <div className="w-fit max-w-[90%] rounded-2xl border border-[#1C2A38]/10 bg-[var(--surface)] px-4 py-2.5 leading-relaxed text-[#1C2A38]">
                For a 5kg joint, a NEMA 17 with a 1:50 harmonic gearbox gives ~22 Nm output with low
                backlash. Pair it with a 48V driver and a 17-bit absolute encoder. Want supplier options?
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <div className="rounded-3xl border border-[#1C2A38]/20 bg-gradient-to-br from-[#1C2A38] to-[#33445a] p-10 text-center shadow-xl sm:p-16">
          <h2 className="font-display text-3xl font-800 tracking-tight text-white sm:text-4xl">
            Build your next robot faster
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-slate-300">
            Join engineers sourcing smarter with ROSA. Free to start.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/register">
              <Button data-testid="cta-register" className="h-12 bg-[var(--teal)] px-7 font-600 text-[#063b34] hover:bg-[#1ee9cf]">
                Get started free
              </Button>
            </Link>
            <Button onClick={() => openAI()} variant="outline" data-testid="cta-ai" className="h-12 border-white/25 bg-transparent px-7 text-white hover:bg-white/10">
              Ask ROSA AI
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
